import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import Button from './Button';
import Input from './Input';
import { ButtonLoading } from './LoadingStates';
import { ErrorSummary, ErrorMessage, ValidationError } from './ErrorStates';

// ===== ENHANCED FORM WRAPPER ===== //
export const EnhancedForm = ({ 
  children,
  onSubmit,
  loading = false,
  loadingMessage = 'Processing...',
  errors = {},
  showErrorSummary = true,
  className,
  ...props 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasErrors = Object.values(errors).some(error => error);
  const isLoading = loading || isSubmitting;

  return (
    <form 
      className={cn('relative space-y-6', className)} 
      onSubmit={handleSubmit}
      noValidate
      {...props}
    >
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-background flex items-center justify-center z-10 rounded-lg">
          <ButtonLoading text={loadingMessage} />
        </div>
      )}

      {/* Error Summary */}
      {showErrorSummary && hasErrors && (
        <ErrorSummary errors={errors} />
      )}

      {/* Form Content */}
      <div className={cn('space-y-4', isLoading && 'opacity-50 pointer-events-none')}>
        {children}
      </div>
    </form>
  );
};

// ===== FORM FIELD WRAPPER ===== //
export const FormField = ({ 
  label,
  htmlFor,
  error,
  required = false,
  description,
  children,
  className 
}) => {
  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label 
          htmlFor={htmlFor}
          className="block text-sm font-medium text-text-primary"
        >
          {label}
          {required && (
            <span className="text-error ml-1" aria-label="required">*</span>
          )}
        </label>
      )}
      
      {description && !error && (
        <p className="text-sm text-secondary">{description}</p>
      )}
      
      {children}
      
      <ValidationError message={error} fieldId={htmlFor} />
    </div>
  );
};

// ===== FORM SECTION ===== //
export const FormSection = ({ 
  title,
  description,
  children,
  error,
  className 
}) => {
  return (
    <div className={cn('space-y-4', className)}>
      {(title || description) && (
        <div className="space-y-1">
          {title && (
            <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
          )}
          {description && (
            <p className="text-sm text-secondary">{description}</p>
          )}
        </div>
      )}
      
      {error && (
        <ErrorMessage message={error} />
      )}
      
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
};

// ===== FORM ACTIONS ===== //
export const FormActions = ({ 
  children,
  align = 'right',
  className 
}) => {
  const alignmentClasses = {
    left: 'justify-start',
    center: 'justify-center', 
    right: 'justify-end',
    apart: 'justify-between'
  };

  return (
    <div className={cn(
      'flex gap-3 pt-4 border-t border-border',
      alignmentClasses[align],
      className
    )}>
      {children}
    </div>
  );
};

// ===== FORM VALIDATION HOOK ===== //
export const useFormValidation = (initialValues = {}, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const setValue = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const setFieldTouched = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const validateField = (field, value) => {
    const rules = validationRules[field];
    if (!rules) return '';

    for (const rule of rules) {
      const error = rule(value, values);
      if (error) return error;
    }
    
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    
    Object.keys(validationRules).forEach(field => {
      const error = validateField(field, values[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  };

  return {
    values,
    errors,
    touched,
    setValue,
    setFieldTouched,
    validateField,
    validateForm,
    resetForm,
    isValid: Object.keys(errors).length === 0
  };
};

// ===== COMMON VALIDATION RULES ===== //
export const validationRules = {
  required: (message = 'This field is required') => (value) => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return message;
    }
    return '';
  },

  email: (message = 'Please enter a valid email address') => (value) => {
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return message;
    }
    return '';
  },

  minLength: (min, message) => (value) => {
    if (value && value.length < min) {
      return message || `Must be at least ${min} characters`;
    }
    return '';
  },

  maxLength: (max, message) => (value) => {
    if (value && value.length > max) {
      return message || `Must be no more than ${max} characters`;
    }
    return '';
  },

  number: (message = 'Please enter a valid number') => (value) => {
    if (value && isNaN(value)) {
      return message;
    }
    return '';
  },

  min: (min, message) => (value) => {
    if (value && Number(value) < min) {
      return message || `Must be at least ${min}`;
    }
    return '';
  },

  max: (max, message) => (value) => {
    if (value && Number(value) > max) {
      return message || `Must be no more than ${max}`;
    }
    return '';
  },

  pattern: (regex, message) => (value) => {
    if (value && !regex.test(value)) {
      return message;
    }
    return '';
  },

  custom: (validator, message) => (value, allValues) => {
    if (!validator(value, allValues)) {
      return message;
    }
    return '';
  }
};

// ===== ASYNC FORM HANDLER ===== //
export const useAsyncFormHandler = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (submitFunction) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await submitFunction();
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  };

  return {
    loading,
    error,
    success,
    handleSubmit,
    reset
  };
};

// ===== FORM STEPPER ===== //
export const FormStepper = ({ 
  steps = [],
  currentStep = 0,
  onStepChange,
  className 
}) => {
  return (
    <div className={cn('flex items-center justify-between mb-8', className)}>
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div 
            className={cn(
              'flex items-center cursor-pointer',
              index <= currentStep ? 'text-primary' : 'text-muted-foreground'
            )}
            onClick={() => onStepChange && onStepChange(index)}
          >
            <div className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors',
              index < currentStep 
                ? 'bg-primary border-primary text-primary-foreground'
                : index === currentStep
                ? 'border-primary text-primary'
                : 'border-muted-foreground text-muted-foreground'
            )}>
              {index < currentStep ? '✓' : index + 1}
            </div>
            <span className="ml-2 text-sm font-medium hidden sm:block">
              {step.title}
            </span>
          </div>
          
          {index < steps.length - 1 && (
            <div className={cn(
              'flex-1 h-px mx-4',
              index < currentStep ? 'bg-primary' : 'bg-muted-foreground'
            )} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
